import { customClickReport } from '@/dataPort';
import { wispClstag } from '@/dataPort/routeTrack';

export const SEARCH_AI_PAGE_ID = 'SearchAi';

export const SEARCH_AI_FLAG_VALUES = {
  AI: 'Y',
  NORMAL: 'N',
} as const;

export type SearchAiFlagValue = (typeof SEARCH_AI_FLAG_VALUES)[keyof typeof SEARCH_AI_FLAG_VALUES];

export const SEARCH_AI_TRACK_EVENTS = {
  entryClick: 'Entry_Click',
  productListViewDetail: 'ProductList_ViewDetail',
  productListAddCart: 'ProductList_AddCart',
  productListAddCartFail: 'ProductList_AddCartFail',
  productDetailAddCart: 'ProductDetail_AddCart',
  productDetailAddCartFail: 'ProductDetail_AddCartFail',
  productDetailBuyNow: 'ProductDetail_BuyNow',
  productDetailBuyNowFail: 'ProductDetail_BuyNowFail',
  matchLoadSuccess: 'SearchAi_MatchLoadSuccess',
  matchLoadFail: 'SearchAi_MatchLoadFail',
  openDetail: 'SearchAi_OpenDetail',
  openFillOrder: 'SearchAi_OpenFillOrder',
  jumpPage: 'SearchAi_JumpPage',
} as const;

/** 与事件 ID 分开命名，避免历史事件名里已有 Click 后再拼出 Click_Click。最后一段仍以 Click 结尾。 */
export const SEARCH_AI_CLSTAG = {
  entry: wispClstag('SearchAi_Entry', 'Click'),
  viewDetail: wispClstag('SearchAi_ViewDetail', 'Click'),
  addCart: wispClstag('SearchAi_AddCart', 'Click'),
  detailAddCart: wispClstag('SearchAi_DetailAddCart', 'Click'),
  buyNow: wispClstag('SearchAi_DetailBuyNow', 'Click'),
} as const;

type SearchAiSkuTrackSource = {
  skuId?: string | number;
  sku_id?: string | number;
  sku?: string | number;
  name?: string;
  skuName?: string;
  sku_name?: string;
  wareName?: string;
  productName?: string;
  skuPrice?: string | number;
  sku_price?: string | number;
  price?: string | number;
  includeTaxPrice?: string | number;
  salePrice?: string | number;
  showCurrency?: {
    salePrice?: string | number;
    includeTaxPrice?: string | number;
    price?: string | number;
  };
};

const pickFirstValue = <T,>(...values: Array<T | null | undefined>) => {
  return values.find(value => value !== null && value !== undefined && value !== '') as T | undefined;
};

const normalizeSkuPrice = (value: unknown) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const parsed = Number(value.replace(/,/g, '').replace(/[^\d.-]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

export const buildSearchAiSkuJsonParam = (
  source: SearchAiSkuTrackSource,
  skuQuantity?: number,
) => {
  const skuPrice = pickFirstValue(
    source.skuPrice,
    source.sku_price,
    source.showCurrency?.salePrice,
    source.showCurrency?.includeTaxPrice,
    source.includeTaxPrice,
    source.salePrice,
    source.price,
    source.showCurrency?.price,
  );

  const jsonParam: Record<string, string | number> = {
    sku_id: String(pickFirstValue(source.skuId, source.sku_id, source.sku) || ''),
    sku_name: String(pickFirstValue(source.name, source.skuName, source.sku_name, source.wareName, source.productName) || ''),
    sku_price: normalizeSkuPrice(skuPrice),
  };

  if (skuQuantity !== undefined) {
    jsonParam.sku_quantity = Math.max(1, Number(skuQuantity) || 1);
  }

  return jsonParam;
};

export const withSearchAiFlag = <T extends Record<string, unknown>>(jsonParam: T, flagValue: SearchAiFlagValue) => ({
  ...jsonParam,
  // PRD 约定 IF_AI 必须以字符串 Y/N 标识是否来自 AI 行家导购，不能复用历史 1/0 口径。
  IF_AI: flagValue,
});

// AI 导购涉及父页面、子应用 iframe、父应用商详 Drawer 多层路由，埋点统一固定到 SearchAi pageId，避免被内嵌路由带偏。
export const reportSearchAiEvent = (
  eventId: (typeof SEARCH_AI_TRACK_EVENTS)[keyof typeof SEARCH_AI_TRACK_EVENTS],
  jsonParam?: Record<string, unknown>,
  clstag?: string,
) => {
  // AI 导购固定 pageId。clstag 放在 payload 顶层，且必须和触发元素上的属性是同一个字符串。
  customClickReport(SEARCH_AI_PAGE_ID, eventId, {
    ...(jsonParam || {}),
    ...(clstag ? { clstag } : {}),
  });
};
